import { useMemo, useCallback, useState, useEffect } from 'react';

//* HOC's
import { withDataContext, withUIContext, withLanguageContext } from 'context';

//* Components
import Text from 'components/common/Text';
import Icon from 'components/common/Icon';
import Button from 'components/common/Button';
import CustomLink from 'components/common/CustomLink';
import SharePopup from 'components/common/Popups/SharePopup';

//* Style
import InfoStyle from './style';

const Info = ({ data, isSticky, addBookmark, checkIsBookmarked, removeBookmark, openPopup, winWidth, screenSizes, translate }) => {
	//! State
	const [isSaved, setIsSaved] = useState(false);

	//! Store tags
	const storeTags = useMemo(() => {
		return data.tags.map((tag) => (
			<Text
				className={'pXS font-helvetica hashtag'}
				key={tag.id}
				text={tag.name}
			/>
		));
	}, [data]);

	//! Open Share
	const openSharePopup = useCallback(() => {
		openPopup(<SharePopup />);
	}, [openPopup]);

	//! Check Is Saved
	useEffect(() => {
		setIsSaved(checkIsBookmarked({ type: data.type, id: data.id }));
	}, []);

	//! Handle Save Button Click
	const handleClickSaveButton = useCallback(() => {
		isSaved ? removeBookmark({ type: data.type, id: data.id }) : addBookmark({ type: data.type, id: data.id });

		setIsSaved(checkIsBookmarked({ type: data.type, id: data.id }));
	}, [data, isSaved]);

	return (
		<InfoStyle className={`info ${isSticky && winWidth < screenSizes.tabletSize ? '' : 'sticky'}`}>
			<Text
				tag={'h1'}
				className={'h4 font-ping-bolder'}
				text={data.title}
			/>

			<div className='hashtags-wrap'>{storeTags}</div>

			{data.duration && (
				<Text
					className={'p font-soundscape-bold big-date'}
					text={data.duration}
				/>
			)}

			<div className='info'>
				{data.filter_type && (
					<div className='info-block'>
						<Text className={'pS uppercase font-ping-bolder'}>{translate('type')}:</Text>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={`${data.filter_type}`}
						/>
					</div>
				)}

				{data?.locations?.length > 0 && (
					<div className='info-block'>
						<Text className={'pS uppercase font-ping-bolder'}>{translate('region')}:</Text>
						<div className='info-block-right'>
							{data.locations.map((item, index) => {
								return (
									<Text
										key={index}
										className={'pS font-helvetica location-item'}>
										{item.name}, <span className='font-helvetica-bold'>{item.region}</span>
									</Text>
								);
							})}
						</div>
					</div>
				)}

				{data.architectural_style && (
					<div className='info-block'>
						<Text
							className={'pS uppercase font-ping-bolder'}
							text={'architecturalStyle'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.architectural_style}
						/>
					</div>
				)}

				{data.date_of_foundation && (
					<div className='info-block'>
						<Text
							className={'pS uppercase font-ping-bolder'}
							text={'dateOfFoundation'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.date_of_foundation}
						/>
					</div>
				)}

				{data.region && (
					<div className='info-block'>
						<Text
							className={'pS uppercase font-ping-bolder'}
							text={'region'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.region}
						/>
					</div>
				)}

				{data.interesting_fact && (
					<div className='info-block'>
						<Text
							className={'pS uppercase font-ping-bolder'}
							text={'interestingFact'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.interesting_fact}
						/>
					</div>
				)}

				{data.date && (
					<div className='info-block'>
						<Text
							className={'pS uppercase font-ping-bolder'}
							text={'date'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.date.replace(',', ', ')}
						/>
					</div>
				)}

				{data.time && (
					<div className='info-block'>
						<Text
							className={'pS uppercase font-ping-bolder'}
							text={'time'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.time}
						/>
					</div>
				)}

				{data.location && (
					<div className='info-block'>
						<Text
							className={'pS left-hand uppercase font-ping-bolder'}
							text={'location'}
						/>

						<Text
							className={'pS font-helvetica info-block-right'}
							text={data.location}
						/>
					</div>
				)}

				{data.entrance_fee && (
					<div className='info-block'>
						<Text
							className={'pS left-hand uppercase font-ping-bolder'}
							text={'entrance'}
						/>

						<Text
							className={'pS font-helvetica entrance-fee info-block-right'}
							text={data.entrance_fee}
						/>
					</div>
				)}

				{data.website && (
					<div className='info-block'>
						<Text
							className={'pS left-hand uppercase font-ping-bolder'}
							text={'website'}
						/>

						<div className='info-block-right'>
							<div className='link-wrap'>
								<CustomLink
									options={{ target: '_blank' }}
									url={data.website}>
									<Text
										className={'pS website font-helvetica-bold underline'}
										text={data.website}
									/>
								</CustomLink>
							</div>
						</div>
					</div>
				)}

				{data.phone && (
					<div className='info-block'>
						<Text
							className={'pS left-hand uppercase font-ping-bolder'}
							text={'phone'}
						/>

						<div className={'info-block-right'}>
							<a
								className='p pS underline font-helvetica-bold link'
								href={`tel:${data.phone}`}>
								{data.phone}
							</a>
						</div>
					</div>
				)}

				{data?.phones?.length > 0 && (
					<div className='info-block'>
						<Text
							className={'pS left-hand uppercase font-ping-bolder'}
							text={'phone'}
						/>
						<div className='info-block-right'>
							{data.phones.map((item, index) => {
								return (
									<a
										key={index}
										className='p pS phone underline font-helvetica-bold link'
										href={`tel:${item}`}>
										{item}
									</a>
								);
							})}
						</div>
					</div>
				)}

				{data.link && (
					<div className='info-block'>
						<Text
							className={'pS left-hand uppercase font-ping-bolder'}
							text={'registerInfo'}
						/>
						<div className='info-block-right'>
							<div className='link-wrap'>
								<CustomLink
									options={{ target: '_blank' }}
									url={data.link}>
									<Text
										className={'pS font-helvetica-bold link underline'}
										text={data.link}
									/>
								</CustomLink>
							</div>
						</div>
					</div>
				)}

				{data.email && (
					<div className='info-block'>
						<Text className={'pS left-hand uppercase font-ping-bolder'}>{translate('email')}:</Text>
						<div className='info-block-right'>
							<a
								className='p pS font-helvetica-bold underline link'
								href={`mailto:${data.email}`}>
								{data.email}
							</a>
						</div>
					</div>
				)}

				{!!data.socials && Object.keys(data.socials).length > 0 && (
					<div className='info-block'>
						<Text className={'pS left-hand uppercase font-ping-bolder'}>{translate('social')}:</Text>
						<div className='info-block-right'>
							<div className='socials-wrap'>
								{Object.keys(data.socials).map((social, index) => (
									<CustomLink
										target={'_blank'}
										url={data.socials[social]}
										key={index}>
										<Icon
											className={'h5'}
											name={social}
										/>
									</CustomLink>
								))}
							</div>
						</div>
					</div>
				)}
			</div>

			<div className='btns-wrap'>
				<Button
					icon={'share'}
					text={'share'}
					className='hero-btn'
					color={'ColorApricot'}
					onClick={openSharePopup}
					borderColor={'transparent'}
					hoverColor={'ColorBigStone'}
					background={'ColorBigStone'}
					hoverBorderColor={'transparent'}
					hoverBackground={'ColorApricot'}
				/>

				<Button
					icon={'bookmarks'}
					className='hero-btn'
					hoverColor={'ColorWhite'}
					borderColor={'ColorApricot'}
					onClick={handleClickSaveButton}
					hoverBackground={'ColorApricot'}
					text={isSaved ? 'saved' : 'save'}
					hoverBorderColor={'ColorApricot'}
					color={isSaved ? 'ColorWhite' : 'ColorApricot'}
					background={isSaved ? 'ColorApricot' : 'transparent'}
				/>
			</div>
		</InfoStyle>
	);
};

export default withLanguageContext(withUIContext(withDataContext(Info, ['addBookmark', 'checkIsBookmarked', 'removeBookmark']), ['openPopup', 'winWidth', 'screenSizes']), ['translate']);
